import { Star, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

const RefundPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="text-[#64748B] hover:text-[#FF69B4]"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#87CEEB]/20 flex items-center justify-center">
              <Star className="w-4 h-4 text-[#FF69B4]" />
            </div>
            <span className="font-outfit font-bold text-lg text-[#1E293B]">Cancellation & Refund Policy</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm">
          <h1 className="font-outfit font-bold text-2xl md:text-3xl text-[#1E293B] mb-6">Cancellation & Refund Policy</h1>
          <p className="text-sm text-[#64748B] mb-6">Last updated: December 2025</p>

          <div className="space-y-6 text-[#475569]">
            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">1. Overview</h2>
              <p>
                This Cancellation and Refund Policy outlines the terms and conditions for cancellations and refunds on the FOUNDSTAR platform. Please read this policy carefully before making any payment.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">2. Verification Fee (For Influencers)</h2>
              <div className="bg-[#FFF0F5] p-4 rounded-xl mb-4">
                <p className="font-medium text-[#1E293B]">Verification Fee: ₹199</p>
              </div>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Non-Refundable:</strong> The verification fee is a one-time payment and is non-refundable once the verification request has been submitted.</li>
                <li><strong>Rejection:</strong> If your verification request is rejected, the fee will not be refunded. However, you may re-apply after addressing the reasons for rejection.</li>
                <li><strong>Technical Issues:</strong> If payment was processed but verification request was not submitted due to technical issues on our end, a full refund will be provided within 7-10 business days.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">3. Collaboration Payments (Escrow System)</h2>
              <p className="mb-3">For brand-influencer collaborations processed through our escrow payment system:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Before Work Starts:</strong> If a collaboration is cancelled before the influencer begins work, 100% of the payment will be refunded to the brand (minus payment gateway charges).</li>
                <li><strong>Work In Progress:</strong> If cancelled during the collaboration, a partial refund may be issued based on the work completed. This will be determined on a case-by-case basis.</li>
                <li><strong>Work Completed:</strong> Once the influencer has delivered the agreed work and it has been approved, no refund will be provided.</li>
                <li><strong>Dispute Resolution:</strong> In case of disputes, both parties can raise a complaint. Our team will review and make a fair decision within 7 business days.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">4. Platform Commission</h2>
              <p>
                FOUNDSTAR charges a platform commission on successful collaborations. This commission is deducted from the payment before releasing funds to the influencer and is non-refundable.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">5. How to Request a Refund</h2>
              <p className="mb-3">To request a refund (where applicable):</p>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Email us at <a href="mailto:support@foundstar.in" className="text-[#FF69B4] hover:underline">support@foundstar.in</a> with your request.</li>
                <li>Include your registered email, transaction ID, and reason for refund.</li>
                <li>Our team will review your request within 3-5 business days.</li>
                <li>If approved, refunds will be processed to the original payment method within 7-10 business days.</li>
              </ol>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">6. Cancellation by FOUNDSTAR</h2>
              <p>
                We reserve the right to cancel any transaction or terminate any account that violates our Terms and Conditions. In such cases, refunds will be provided at our discretion.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">7. Refund Timeline</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-200 mt-3">
                  <thead>
                    <tr className="bg-[#F8FAFC]">
                      <th className="border border-gray-200 px-4 py-2 text-left">Payment Method</th>
                      <th className="border border-gray-200 px-4 py-2 text-left">Refund Timeline</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-200 px-4 py-2">Credit/Debit Card</td>
                      <td className="border border-gray-200 px-4 py-2">5-7 business days</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-4 py-2">Net Banking</td>
                      <td className="border border-gray-200 px-4 py-2">5-7 business days</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-4 py-2">UPI</td>
                      <td className="border border-gray-200 px-4 py-2">2-3 business days</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-4 py-2">Wallets</td>
                      <td className="border border-gray-200 px-4 py-2">2-3 business days</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">8. Contact Us</h2>
              <p>
                For any questions regarding this policy, please contact us at:
              </p>
              <p className="mt-2">
                <strong>Email:</strong> <a href="mailto:support@foundstar.in" className="text-[#FF69B4] hover:underline">support@foundstar.in</a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RefundPolicy;
